import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../../config/URL";
import toast from "react-hot-toast";
import { FiAlertTriangle } from "react-icons/fi";
import "../../../styles/admin.css";
import { MultiSelect } from "react-multi-select-component";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import ImageURL from "../../../config/ImageURL";
import NoImage from "../../../assets/noImage.webp";

function VoucherEdit() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [applyToAll, setApplyToAll] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [applyToAllUsers, setApplyToAllUsers] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const SUPPORTED_FORMATS = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/webp",
  ];

  useEffect(() => {
    api.get("admin/getAllProductList").then((response) => {
      const productData = response.data.data || [];
      setAllProducts(
        productData.map((product) => ({
          value: product.value,
          label: product.label,
        }))
      );
    });
    api.get("admin/getAllUserList").then((response) => {
      const userData = response.data.data || [];
      setAllUsers(
        userData.map((user) => ({
          value: user.value,
          label: user.label,
        }))
      );
    });
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string().required("*Title field is required"),
    type: Yup.string().required("*Select a type"),
    amount: Yup.number()
      .typeError("*Amount must be a number")
      .required("*Amount is required")
      .positive("*Amount must be greater than zero")
      .when("type", {
        is: "percentage",
        then: (schema) =>
          schema.max(100, "*Percentage cannot be more than 100"),
        otherwise: (schema) => schema,
      }),
    code: Yup.string()
      .matches(/^[^\s]+$/, "*Code must not contain spaces")
      .required("*Code is required"),
    start_date: Yup.string().required("Select a start date"),
    end_date: Yup.string()
      .required("Select an end date")
      .test(
        "is-after-start",
        "End date must be after the start date",
        function (value) {
          const { start_date } = this.parent;
          return (
            !value || !start_date || new Date(value) > new Date(start_date)
          );
        }
      ),
    terms: Yup.string().required("*Terms are required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      code: "",
      type: "",
      amount: "",
      terms: "",
      start_date: "",
      end_date: "",
      image: null,
      min_pruchase_amount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("image", values.image);
      formData.append("title", values.title);
      formData.append("type", values.type);
      formData.append("amount", values.amount);
      formData.append("code", values.code || "");
      formData.append("description", values.description || "");
      formData.append("terms", values.terms || "");
      formData.append("apply_to_all_products", applyToAll ? "1" : "0");
      formData.append("apply_to_all_users", applyToAllUsers ? "1" : "0");
      formData.append("start_date", values.start_date || "");
      formData.append("end_date", values.end_date || "");
      formData.append("min_pruchase_amount", values.min_pruchase_amount || "");

      if (!applyToAll && selectedProducts?.length > 0) {
        selectedProducts.forEach((product, index) => {
          formData.append(`product_ids[${index}]`, product.value);
        });
      }

      if (!applyToAllUsers && selectedUsers?.length > 0) {
        selectedUsers.forEach((user, index) => {
          formData.append(`user_ids[${index}]`, user.value);
        });
      }

      setLoadIndicator(true);

      try {
        const response = await api.post(`admin/vouchers/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/voucher");
          resetForm();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 422) {
            const errors = error.response.data.error;
            if (errors) {
              Object.keys(errors).forEach((key) => {
                errors[key].forEach((errorMsg) => {
                  toast(errorMsg, {
                    icon: <FiAlertTriangle className="text-warning" />,
                  });
                });
              });
            }
          } else {
            toast.error(
              error.response.data.message || "An unexpected error occurred."
            );
          }
        } else {
          toast.error("An unexpected error occurred.");
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`admin/vouchers/${id}`);
        const voucherData = response.data.data;

        formik.setValues({
          title: voucherData.title,
          description: voucherData.description,
          code: voucherData.code,
          type: voucherData.type,
          amount: voucherData.amount,
          terms: voucherData.terms,
          start_date: voucherData.start_date,
          end_date: voucherData.end_date,
          min_pruchase_amount: voucherData.min_pruchase_amount,
          image: null,
        });

        if (!voucherData.apply_to_all_products) {
          setApplyToAll(false);
          const selected = voucherData.products.map((p) => ({
            label: p.name,
            value: p.id,
          }));
          setSelectedProducts(selected);
        }

        if (!voucherData.apply_to_all_users) {
          setApplyToAllUsers(false);
          const selected = voucherData.users.map((u) => ({
            label: u.name,
            value: u.id,
          }));
          setSelectedUsers(selected);
        }

        setPreviewImage(`${ImageURL}${voucherData.image}`);
      } catch (error) {}
      setLoading(false);
    };
    getData();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.currentTarget?.files[0];
    formik.setFieldValue("image", file);
  };

  const Editor = {
    modules: {
      toolbar: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link", "image", "video"],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
      },
    },
  };

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="card card-shadow py-1">
          <div className="row p-3">
            <div className="d-flex justify-content-between align-items-center">
              <h1 className="h4 ls-tight">Update Voucher</h1>
              <Link to="/voucher">
                <button type="button" className="btn btn-light btn-sm">
                  <span>Back</span>
                </button>
              </Link>
            </div>
          </div>
        </div>

        <div className="container card card-shadow form-container">
          {loading ? (
            <div className="loader-container">
              <div className="loader">
                <svg viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="32"></circle>
                </svg>
              </div>
            </div>
          ) : (
            <>
              <div className="row py-4">
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Title</label>
                  <span className="text-danger">*</span>
                  <input
                    type="text"
                    placeholder="Title"
                    className={`form-control ${
                      formik.touched.title && formik.errors.title
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("title")}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <div className="invalid-feedback">
                      {formik.errors.title}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Image
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".png,.jpeg,.jpg,.svg,.webp"
                    className={`form-control ${
                      formik.touched.image && formik.errors.image
                        ? "is-invalid"
                        : ""
                    }`}
                    name="image"
                    onChange={handleFileChange}
                    onBlur={formik.handleBlur}
                  />
                  {previewImage && (
                    <img
                      src={previewImage || NoImage}
                      alt="Preview"
                      style={{ maxWidth: "150px", maxHeight: "100px", marginTop: "10px" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = NoImage;
                      }}
                    />
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">
                    Type<span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      formik.touched.type && formik.errors.type
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("type")}
                  >
                    <option value="">Select a type</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  {formik.touched.type && formik.errors.type && (
                    <div className="invalid-feedback">{formik.errors.type}</div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Amount</label>
                  <span className="text-danger">*</span>
                  <input
                    type="number"
                    placeholder="Amount"
                    className={`form-control ${
                      formik.touched.amount && formik.errors.amount
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("amount")}
                    max={formik.values.type === "percentage" ? 100 : undefined}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <div className="invalid-feedback">
                      {formik.errors.amount}
                    </div>
                  )}
                  {formik.values.type === "percentage" && (
                    <small className="text-muted">
                      Maximum allowed is 100%
                    </small>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Start Date<span className="text-danger">*</span>
                  </label>
                  <div className="custom-date-picker">
                    <DatePicker
                      selected={
                        formik.values.start_date
                          ? new Date(formik.values.start_date)
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          formik.setFieldValue("start_date", formattedDate);
                        } else {
                          formik.setFieldValue("start_date", "");
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      className={`form-control ${
                        formik.touched.start_date && formik.errors.start_date
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholderText="Select a start date"
                    />
                  </div>
                  {formik.touched.start_date && formik.errors.start_date && (
                    <div className="text-danger mt-1">
                      {formik.errors.start_date}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    End Date<span className="text-danger">*</span>
                  </label>
                  <div className="custom-date-picker">
                    <DatePicker
                      selected={
                        formik.values.end_date
                          ? new Date(formik.values.end_date)
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          formik.setFieldValue("end_date", formattedDate);
                        } else {
                          formik.setFieldValue("end_date", "");
                        }
                      }}
                      minDate={
                        formik.values.start_date
                          ? new Date(formik.values.start_date)
                          : new Date()
                      }
                      dateFormat="yyyy-MM-dd"
                      className={`form-control ${
                        formik.touched.end_date && formik.errors.end_date
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholderText="Select an end date"
                    />
                  </div>
                  {formik.touched.end_date && formik.errors.end_date && (
                    <div className="text-danger mt-1">
                      {formik.errors.end_date}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Code</label>
                  <span className="text-danger">*</span>
                  <input
                    type="text"
                    placeholder="Code"
                    className={`form-control ${
                      formik.touched.code && formik.errors.code
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("code")}
                  />
                  {formik.touched.code && formik.errors.code && (
                    <div className="invalid-feedback">{formik.errors.code}</div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Tell Us Something...."
                    className={`form-control ${
                      formik.touched.description && formik.errors.description
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("description")}
                    value={formik.values.description}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className="invalid-feedback">
                      {formik.errors.description}
                    </div>
                  )}
                </div>
                {/* Terms */}
                <div className="col-md-12 col-12 ">
                  <div className="mb-5">
                    <label className="form-label">
                      <p className=" ">
                        Terms<span className="text-danger">*</span>
                      </p>
                    </label>
                    <ReactQuill
                      value={formik.values.terms}
                      onChange={(newContent) => {
                        formik.setFieldValue("terms", newContent);
                        formik.validateField("terms");
                      }}
                      onBlur={() => formik.setFieldTouched("terms", true)}
                      modules={Editor.modules}
                    />
                    <div className="error text-danger">
                      <small>
                        {formik.touched.terms && formik.errors.terms}
                      </small>
                    </div>
                  </div>
                </div>
                {/* All Products */}
                <div className="col-md-6 col-12 file-input mb-5">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={applyToAll}
                      onChange={(e) => setApplyToAll(e.target.checked)}
                      id="apply_to_all_products"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="apply_to_all_products"
                    >
                      Apply to all products
                    </label>
                  </div>
                  {!applyToAll && (
                    <>
                      <label className="form-label">Select Products</label>
                      <MultiSelect
                        isMulti
                        options={allProducts}
                        value={selectedProducts}
                        onChange={(selectedOptions) =>
                          setSelectedProducts(selectedOptions)
                        }
                        placeholder="Search and select products..."
                        isSearchable
                        className="react-select"
                        classNamePrefix="select"
                      />
                    </>
                  )}
                </div>
                {/* All Users */}
                <div className="col-md-6 col-12 file-input mb-5">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={applyToAllUsers}
                      onChange={(e) => setApplyToAllUsers(e.target.checked)}
                      id="apply_to_all_users"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="apply_to_all_users"
                    >
                      Apply to all users
                    </label>
                  </div>
                  {!applyToAllUsers && (
                    <>
                      <label className="form-label">Select Users</label>
                      <MultiSelect
                        isMulti
                        options={allUsers}
                        value={selectedUsers}
                        onChange={(selectedOptions) =>
                          setSelectedUsers(selectedOptions)
                        }
                        placeholder="Search and select users..."
                        isSearchable
                        className="react-select"
                        classNamePrefix="select"
                      />
                    </>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">
                    Minimum Purchases Amount(optional)
                  </label>
                  <input
                    type="number"
                    placeholder="Minimum Purchases Amount"
                    className={`form-control ${
                      formik.touched.min_pruchase_amount &&
                      formik.errors.min_pruchase_amount
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("min_pruchase_amount")}
                  />
                  {formik.touched.min_pruchase_amount &&
                    formik.errors.min_pruchase_amount && (
                      <div className="invalid-feedback">
                        {formik.errors.min_pruchase_amount}
                      </div>
                    )}
                </div>
              </div>
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button mt-5"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Submit
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </section>
  );
}

export default VoucherEdit;
